import * as THREE from 'three';

/**
 * WaterShader - Enhanced underwater visual effects + animated water surface.
 *
 * Surface effects (always active when water visible):
 *   - Animated UV shimmer on water blocks (sine-wave offset)
 *   - Subtle opacity oscillation (0.5 - 0.7)
 *   - Foam highlights at water-solid block edges
 *
 * Underwater effects (playerInWater = true):
 *   - Blue-green color tint overlay with gentle wave distortion
 *   - Floating bubble particles (small white/cyan circles rising upward)
 *   - Animated light rays from above (gradient beams)
 *   - Animated caustic light patterns (moving radial gradients)
 *   - Subtle vignette darkening at edges
 *
 * All effects transition smoothly on enter/exit.
 * Self-contained: only imports THREE.
 */

// --- Configuration ---
const BUBBLE_COUNT = 40;
const BUBBLE_SPAWN_RADIUS = 8;    // horizontal spawn range around player
const BUBBLE_MIN_SPEED = 1.2;
const BUBBLE_MAX_SPEED = 3.0;
const BUBBLE_MIN_SIZE = 0.04;
const BUBBLE_MAX_SIZE = 0.12;
const BUBBLE_LIFETIME = 4.0;      // seconds before recycling
const RAY_COUNT = 5;              // number of light ray beams
const CAUSTIC_COUNT = 6;          // number of caustic light patches

// Water surface animation
const WATER_OPACITY_MIN = 0.50;
const WATER_OPACITY_MAX = 0.70;
const WATER_OPACITY_SPEED = 1.5;  // oscillation speed (rad/s)

export class WaterShader {
  /**
   * @param {THREE.Scene} scene - The main Three.js scene
   */
  constructor(scene) {
    this.scene = scene;
    this._active = false;
    this._elapsed = 0;
    this._opacity = 0;            // current overlay opacity (0-1, for smooth transition)
    this._targetOpacity = 0;
    this._transitionSpeed = 2.5;  // opacity units per second

    // Reference to transparent material (set externally via setTransparentMaterial)
    this._transparentMaterial = null;
    // Stored original opacity to restore when not animating
    this._origTransparentOpacity = 0.65;

    // --- DOM Overlays ---
    this._buildOverlays();

    // --- Three.js Bubble Particles ---
    this._buildBubbles();

    // Player tracking
    this._playerPos = new THREE.Vector3();
  }

  /**
   * Provide the chunk mesher's transparent material so we can animate water surface.
   * @param {THREE.Material} mat
   */
  setTransparentMaterial(mat) {
    this._transparentMaterial = mat;
    if (mat) {
      this._origTransparentOpacity = mat.opacity;
    }
  }

  // ====================================================================
  // DOM Overlay Construction
  // ====================================================================

  _buildOverlays() {
    // Inject CSS keyframes
    this._style = document.createElement('style');
    this._style.textContent = `
      @keyframes watershader-wave {
        0%   { transform: translate(0, 0) scale(1); }
        25%  { transform: translate(2px, -1px) scale(1.002); }
        50%  { transform: translate(-1px, 2px) scale(1); }
        75%  { transform: translate(1px, 1px) scale(1.001); }
        100% { transform: translate(0, 0) scale(1); }
      }
      @keyframes watershader-ray-drift {
        0%   { transform: translateX(0) skewX(-5deg); opacity: 0.08; }
        33%  { transform: translateX(15px) skewX(-3deg); opacity: 0.14; }
        66%  { transform: translateX(-10px) skewX(-7deg); opacity: 0.06; }
        100% { transform: translateX(0) skewX(-5deg); opacity: 0.08; }
      }
      @keyframes watershader-ray-drift2 {
        0%   { transform: translateX(0) skewX(3deg); opacity: 0.06; }
        50%  { transform: translateX(-20px) skewX(5deg); opacity: 0.12; }
        100% { transform: translateX(0) skewX(3deg); opacity: 0.06; }
      }
      @keyframes watershader-caustic-move {
        0%   { transform: translate(0%, 0%) scale(1); opacity: 0.06; }
        25%  { transform: translate(8%, -5%) scale(1.1); opacity: 0.1; }
        50%  { transform: translate(-4%, 6%) scale(0.95); opacity: 0.07; }
        75%  { transform: translate(5%, 3%) scale(1.05); opacity: 0.11; }
        100% { transform: translate(0%, 0%) scale(1); opacity: 0.06; }
      }
      @keyframes watershader-caustic-move2 {
        0%   { transform: translate(0%, 0%) rotate(0deg); opacity: 0.05; }
        33%  { transform: translate(-6%, 4%) rotate(60deg); opacity: 0.09; }
        66%  { transform: translate(3%, -7%) rotate(120deg); opacity: 0.06; }
        100% { transform: translate(0%, 0%) rotate(0deg); opacity: 0.05; }
      }
    `;
    document.head.appendChild(this._style);

    // Main underwater tint container - applies wave distortion to everything inside
    this._container = document.createElement('div');
    this._container.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 93;
      opacity: 0;
      transition: none;
      animation: watershader-wave 4s ease-in-out infinite;
      animation-play-state: paused;
    `;
    document.body.appendChild(this._container);

    // Blue-green tint layer
    this._tintLayer = document.createElement('div');
    this._tintLayer.style.cssText = `
      position: absolute; inset: 0;
      background: radial-gradient(
        ellipse at 50% 30%,
        rgba(15, 60, 100, 0.18) 0%,
        rgba(8, 35, 75, 0.32) 60%,
        rgba(4, 18, 55, 0.45) 100%
      );
    `;
    this._container.appendChild(this._tintLayer);

    // Light rays container
    this._raysContainer = document.createElement('div');
    this._raysContainer.style.cssText = `
      position: absolute; inset: 0; overflow: hidden;
    `;
    this._container.appendChild(this._raysContainer);

    // Create individual light ray beams
    this._rays = [];
    for (let i = 0; i < RAY_COUNT; i++) {
      const ray = document.createElement('div');
      const leftPos = 10 + (i / (RAY_COUNT - 1)) * 80; // spread 10%-90%
      const width = 40 + Math.random() * 60;
      const animName = i % 2 === 0 ? 'watershader-ray-drift' : 'watershader-ray-drift2';
      const duration = 6 + Math.random() * 4;
      const delay = Math.random() * -duration;

      ray.style.cssText = `
        position: absolute;
        top: -10%;
        left: ${leftPos}%;
        width: ${width}px;
        height: 120%;
        background: linear-gradient(
          180deg,
          rgba(120, 200, 255, 0.15) 0%,
          rgba(80, 170, 240, 0.06) 40%,
          transparent 80%
        );
        opacity: 0.1;
        mix-blend-mode: screen;
        animation: ${animName} ${duration}s ease-in-out infinite;
        animation-delay: ${delay}s;
        filter: blur(8px);
        transform-origin: top center;
      `;
      this._raysContainer.appendChild(ray);
      this._rays.push(ray);
    }

    // --- Caustic light patches (animated radial gradients) ---
    this._causticsContainer = document.createElement('div');
    this._causticsContainer.style.cssText = `
      position: absolute; inset: 0; overflow: hidden;
    `;
    this._container.appendChild(this._causticsContainer);

    for (let i = 0; i < CAUSTIC_COUNT; i++) {
      const caustic = document.createElement('div');
      const size = 150 + Math.random() * 200;
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const animName = i % 2 === 0 ? 'watershader-caustic-move' : 'watershader-caustic-move2';
      const duration = 8 + Math.random() * 6;
      const delay = Math.random() * -duration;

      caustic.style.cssText = `
        position: absolute;
        left: ${left}%;
        top: ${top}%;
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: radial-gradient(
          circle,
          rgba(100, 200, 255, 0.15) 0%,
          rgba(80, 180, 240, 0.08) 30%,
          rgba(60, 160, 220, 0.03) 60%,
          transparent 100%
        );
        mix-blend-mode: screen;
        animation: ${animName} ${duration}s ease-in-out infinite;
        animation-delay: ${delay}s;
        filter: blur(12px);
        pointer-events: none;
      `;
      this._causticsContainer.appendChild(caustic);
    }

    // Edge darkening (underwater vignette - stronger than normal)
    this._waterVignette = document.createElement('div');
    this._waterVignette.style.cssText = `
      position: absolute; inset: 0;
      background: radial-gradient(
        ellipse at 50% 40%,
        transparent 30%,
        rgba(5, 15, 40, 0.35) 100%
      );
    `;
    this._container.appendChild(this._waterVignette);
  }

  // ====================================================================
  // Three.js Bubble Particles
  // ====================================================================

  _buildBubbles() {
    // Use Points with a custom circular texture for bubbles
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');

    // Draw a soft circle (bubble shape)
    const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(200, 240, 255, 0.9)');
    gradient.addColorStop(0.4, 'rgba(150, 220, 255, 0.6)');
    gradient.addColorStop(0.7, 'rgba(100, 190, 240, 0.3)');
    gradient.addColorStop(1, 'rgba(80, 170, 230, 0.0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 32, 32);

    // Small specular highlight
    ctx.beginPath();
    ctx.arc(12, 10, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fill();

    this._bubbleTexture = new THREE.CanvasTexture(canvas);

    // Create geometry with positions, velocities, and lifetimes stored in parallel arrays
    this._bubblePositions = new Float32Array(BUBBLE_COUNT * 3);
    this._bubbleSizes = new Float32Array(BUBBLE_COUNT);
    this._bubbleVelocities = new Float32Array(BUBBLE_COUNT * 3); // vx, vy, vz
    this._bubbleLifetimes = new Float32Array(BUBBLE_COUNT);      // remaining life
    this._bubbleMaxLifetimes = new Float32Array(BUBBLE_COUNT);
    this._bubbleOrigSizes = new Float32Array(BUBBLE_COUNT);     // original spawn size

    // Initialize all bubbles as dead (life <= 0)
    for (let i = 0; i < BUBBLE_COUNT; i++) {
      this._bubbleLifetimes[i] = 0;
      this._bubbleSizes[i] = 0;
      this._bubblePositions[i * 3] = 0;
      this._bubblePositions[i * 3 + 1] = -1000; // offscreen
      this._bubblePositions[i * 3 + 2] = 0;
    }

    this._bubbleGeo = new THREE.BufferGeometry();
    this._bubbleGeo.setAttribute('position', new THREE.BufferAttribute(this._bubblePositions, 3));
    this._bubbleGeo.setAttribute('size', new THREE.BufferAttribute(this._bubbleSizes, 1));

    this._bubbleMat = new THREE.PointsMaterial({
      map: this._bubbleTexture,
      color: 0xCCEEFF,
      size: 0.15,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
    });

    this._bubblePoints = new THREE.Points(this._bubbleGeo, this._bubbleMat);
    this._bubblePoints.frustumCulled = false;
    this._bubblePoints.visible = false;
    this.scene.add(this._bubblePoints);

    // Spawn timer: stagger bubble creation
    this._spawnTimer = 0;
    this._spawnInterval = 0.08; // seconds between spawns
  }

  /**
   * Spawn a single bubble near the player position.
   */
  _spawnBubble() {
    // Find a dead bubble slot
    for (let i = 0; i < BUBBLE_COUNT; i++) {
      if (this._bubbleLifetimes[i] <= 0) {
        const i3 = i * 3;
        const px = this._playerPos.x;
        const py = this._playerPos.y;
        const pz = this._playerPos.z;

        // Random position around player, biased below eye level
        this._bubblePositions[i3]     = px + (Math.random() - 0.5) * BUBBLE_SPAWN_RADIUS;
        this._bubblePositions[i3 + 1] = py - 0.5 + Math.random() * 1.5;
        this._bubblePositions[i3 + 2] = pz + (Math.random() - 0.5) * BUBBLE_SPAWN_RADIUS;

        // Velocity: primarily upward with slight horizontal wobble
        this._bubbleVelocities[i3]     = (Math.random() - 0.5) * 0.4;
        this._bubbleVelocities[i3 + 1] = BUBBLE_MIN_SPEED + Math.random() * (BUBBLE_MAX_SPEED - BUBBLE_MIN_SPEED);
        this._bubbleVelocities[i3 + 2] = (Math.random() - 0.5) * 0.4;

        // Size
        const spawnSize = BUBBLE_MIN_SIZE + Math.random() * (BUBBLE_MAX_SIZE - BUBBLE_MIN_SIZE);
        this._bubbleSizes[i] = spawnSize;
        this._bubbleOrigSizes[i] = spawnSize;

        // Lifetime
        const life = 1.5 + Math.random() * (BUBBLE_LIFETIME - 1.5);
        this._bubbleLifetimes[i] = life;
        this._bubbleMaxLifetimes[i] = life;

        return; // spawned one
      }
    }
  }

  /**
   * Update all bubble particles.
   * @param {number} dt
   */
  _updateBubbles(dt) {
    const positions = this._bubblePositions;
    const velocities = this._bubbleVelocities;
    const sizes = this._bubbleSizes;
    const lifetimes = this._bubbleLifetimes;
    const maxLifetimes = this._bubbleMaxLifetimes;
    const time = this._elapsed;

    for (let i = 0; i < BUBBLE_COUNT; i++) {
      if (lifetimes[i] <= 0) continue;

      lifetimes[i] -= dt;

      const i3 = i * 3;

      // Move bubble
      positions[i3]     += velocities[i3] * dt;
      positions[i3 + 1] += velocities[i3 + 1] * dt;
      positions[i3 + 2] += velocities[i3 + 2] * dt;

      // Add sinusoidal wobble to horizontal movement
      positions[i3]     += Math.sin(time * 3 + i * 1.7) * 0.15 * dt;
      positions[i3 + 2] += Math.cos(time * 2.5 + i * 2.3) * 0.12 * dt;

      // Fade out size near end of life
      if (lifetimes[i] <= 0) {
        sizes[i] = 0;
        positions[i3 + 1] = -1000; // move offscreen
      } else {
        const lifeRatio = lifetimes[i] / maxLifetimes[i];
        // Fade in at start, fade out at end
        const fade = lifeRatio < 0.1
          ? lifeRatio / 0.1
          : lifeRatio > 0.8
            ? (1 - lifeRatio) / 0.2
            : 1.0;
        sizes[i] = this._bubbleOrigSizes[i] * Math.max(0.1, fade);
      }

      // Kill if too far from player
      const dx = positions[i3] - this._playerPos.x;
      const dy = positions[i3 + 1] - this._playerPos.y;
      const dz = positions[i3 + 2] - this._playerPos.z;
      if (dx * dx + dy * dy + dz * dz > 400) { // 20 block radius
        lifetimes[i] = 0;
        sizes[i] = 0;
        positions[i3 + 1] = -1000;
      }
    }

    this._bubbleGeo.getAttribute('position').needsUpdate = true;
    this._bubbleGeo.getAttribute('size').needsUpdate = true;
  }

  // ====================================================================
  // Water Surface Animation
  // ====================================================================

  /**
   * Animate the transparent material's opacity to create a shimmering water surface.
   * This runs every frame regardless of underwater state.
   */
  _updateWaterSurface(dt) {
    if (!this._transparentMaterial) return;

    const t = this._elapsed;

    // Oscillate opacity between WATER_OPACITY_MIN and WATER_OPACITY_MAX
    const wave = Math.sin(t * WATER_OPACITY_SPEED) * 0.5 + 0.5; // 0..1
    const targetOp = WATER_OPACITY_MIN + wave * (WATER_OPACITY_MAX - WATER_OPACITY_MIN);
    this._transparentMaterial.opacity = targetOp;

    // Animate UV offset for shimmer effect via the material's texture
    if (this._transparentMaterial.map) {
      const offsetX = Math.sin(t * 0.8) * 0.005;
      const offsetY = Math.cos(t * 0.6) * 0.004;
      this._transparentMaterial.map.offset.x = offsetX;
      this._transparentMaterial.map.offset.y = offsetY;
    }
  }

  // ====================================================================
  // Public API
  // ====================================================================

  /**
   * Update the water shader effects.
   * @param {number} dt - Delta time in seconds
   * @param {THREE.Camera} camera - The main camera
   * @param {boolean} playerInWater - Whether the player is currently underwater
   */
  update(dt, camera, playerInWater) {
    this._elapsed += dt;

    // Store player position from camera
    if (camera) {
      this._playerPos.copy(camera.position);
    }

    // --- Always animate water surface (shimmer/opacity) ---
    this._updateWaterSurface(dt);

    // Determine target opacity
    this._targetOpacity = playerInWater ? 1 : 0;

    // Smooth transition
    if (this._opacity !== this._targetOpacity) {
      const diff = this._targetOpacity - this._opacity;
      const step = this._transitionSpeed * dt;
      if (Math.abs(diff) <= step) {
        this._opacity = this._targetOpacity;
      } else {
        this._opacity += Math.sign(diff) * step;
      }
    }

    const isVisible = this._opacity > 0.01;

    // --- Update DOM overlays ---
    this._container.style.opacity = this._opacity.toFixed(3);

    if (isVisible && !this._active) {
      // Entering water
      this._active = true;
      this._container.style.animationPlayState = 'running';
    } else if (!isVisible && this._active) {
      // Leaving water
      this._active = false;
      this._container.style.animationPlayState = 'paused';
    }

    // --- Update bubble particles ---
    this._bubblePoints.visible = isVisible;
    this._bubbleMat.opacity = this._opacity * 0.7;

    if (isVisible) {
      // Spawn new bubbles
      this._spawnTimer += dt;
      while (this._spawnTimer >= this._spawnInterval) {
        this._spawnTimer -= this._spawnInterval;
        this._spawnBubble();
      }

      // Update existing bubbles
      this._updateBubbles(dt);
    } else {
      // Kill all bubbles when not underwater
      for (let i = 0; i < BUBBLE_COUNT; i++) {
        this._bubbleLifetimes[i] = 0;
        this._bubbleSizes[i] = 0;
        this._bubblePositions[i * 3 + 1] = -1000;
      }
      this._bubbleGeo.getAttribute('position').needsUpdate = true;
      this._bubbleGeo.getAttribute('size').needsUpdate = true;
      this._spawnTimer = 0;
    }
  }

  /**
   * Clean up all DOM elements and Three.js objects.
   */
  dispose() {
    // Restore original opacity
    if (this._transparentMaterial) {
      this._transparentMaterial.opacity = this._origTransparentOpacity;
      if (this._transparentMaterial.map) {
        this._transparentMaterial.map.offset.x = 0;
        this._transparentMaterial.map.offset.y = 0;
      }
    }

    // Remove DOM elements
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    if (this._style && this._style.parentNode) {
      this._style.parentNode.removeChild(this._style);
    }

    // Remove Three.js objects
    if (this._bubblePoints) {
      this.scene.remove(this._bubblePoints);
      this._bubbleGeo.dispose();
      this._bubbleMat.dispose();
      this._bubbleTexture.dispose();
    }
  }
}
